// @ts-ignore
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { checkboxState, cifTypeState, geoState, overlayCountState, poiMapColorState, selectedTabState, selAggLayerState } from "../../states";
import { GeoJSONLayer, DeckHexagonLayer, MBPLayer, PoILayer, StoryLayer } from ".";
import { useLocation, useSearchParams } from "react-router-dom";
import { getCurrLevel } from "../../helpers";
import { DeckPathLayer } from "./PathLayer";

export const DeckGLOverlay = ({
    geoCode,
    showPoi,
    showMbp,
    showStories,
    poi,
    mbp,
    stories,
    selectedStory,
    onHoverPoi,
    handleClick,
    focusedItem,
    brand,
    presence,
    geoJSON,
    onMapHover,
    onMapClick,
    isGeoFocused,
    mapProps,
}: {
    geoCode: number;
    showPoi: boolean;
    showMbp: boolean | undefined;
    showStories: boolean;
    poi: any[];
    mbp: any[] | undefined;
    stories?: any[];
    selectedStory?: any;
    onHoverPoi?: (type: "poi" | "mbp", poi: any, pixel: number[]) => void;
    handleClick?: (type: "poi" | "mbp" | null, data: any, pixel: number[]) => void;
    brand: any;
    presence: any;
    focusedItem: {
        type: "poi" | "mbp" | null;
        data: any;
    },
    geoJSON?: any;
    onMapHover: (x: number, y: number, object: any, selectedTab: number) => void;
    onMapClick: (geoId: number, geoLevel: string, coordinates: { x: number, y: number }, object: any) => void;
    isGeoFocused: boolean;
    mapProps?: any;
}) => {
    const map = useMap();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get("catchment");
    const cifType = useRecoilValue(cifTypeState);
    const selectedTab = useRecoilValue(selectedTabState);
    const selectedGeo = useRecoilValue(geoState);
    const poiColors = useRecoilValue(poiMapColorState);
    const isChecked = useRecoilValue(checkboxState);
    const overlayCount = useRecoilValue(overlayCountState);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const showHex = selAggLayer?.key === 'hexgrid';

    const overlay = useMemo(() => new GoogleMapsOverlay({
        interleaved: true,
        deviceProps: { waitForPageLoad: false },
        getCursor: ({ isHovering }: any) => {
            if (map) {
                if (isHovering) {
                    map.setOptions({ draggableCursor: "pointer" });
                } else {
                    map.setOptions({ draggableCursor: "" });
                }
            }
            return "inherit";
        }
    }), [map]);

    useEffect(() => {
        if (map) overlay.setMap(map);
        else overlay.setMap(null);

        return () => {
            overlay.setProps({ layers: [] });
            overlay.finalize();
            overlay.setMap(null);
        };
    }, [map, overlay]);

    useEffect(() => {
        const layers: any[] = [];

        const handleGeoHover = (info: any) => {
            if (!isGeoFocused) {
                const { object, picked, x, y } = info;
                if (picked && object) {
                    if (!isChecked?.subregionBoundaries && ((!isChecked?.catchments && Number(selectedGeo?.sub_district) === geoCode) || catchment)) return;
                    onMapHover(x, y, object.properties, selectedTab);
                } else {
                    // Mouse left the geojson layer, clear hover data
                    onMapHover(x, y, null, selectedTab);
                }
            }
        };

        const handleGeoClick = (info: any) => {
            if (info.picked && info.object) {
                if (!isChecked?.subregionBoundaries && ((!isChecked?.catchments && Number(selectedGeo?.sub_district) === geoCode) || catchment)) return;

                // Handle both old structure (geoId, geoLevel) and new structure (hexGeoId, geoValue)
                let { geoId, geoLevel, parent_id, parent_hierarchy_lvl, hexGeoId, geoValue } = info.object.properties;

                // Use hexGeoId and geoValue if they exist (new structure), otherwise use geoId and geoLevel
                const actualGeoId = hexGeoId || geoId;
                const actualGeoLevel = geoValue || geoLevel;

                if (actualGeoId == geoCode) return;

                if (Number(selectedGeo.country) === geoCode) {
                    geoId = parent_id;
                    geoLevel = getCurrLevel(parent_hierarchy_lvl);
                }

                onMapClick(actualGeoId, actualGeoLevel, { x: info.x, y: info.y }, info.object.properties);
            }
        };

        // Add Hexagon layer first (bottom layer)
        const hexagonLayer = DeckHexagonLayer({
            geoJSON,
            selectedGeo,
            selectedTab,
            poiColors,
            cifType,
            isChecked,
            onHover: handleGeoHover,
            onClick: handleGeoClick,
            focusedItem,
            isGeoFocused,
            overlayCount,
            catchment,
            showHex, // Pass showHex to control fill opacity
        });
        if (hexagonLayer && showHex) layers.push(hexagonLayer);

        // Add GeoJSON layer on top (boundaries visible above hexagons)
        const geoLayer = GeoJSONLayer({
            geoJSON,
            selectedGeo,
            selectedTab,
            poiColors,
            cifType,
            isChecked,
            onHover: handleGeoHover,
            onClick: handleGeoClick,
            focusedItem,
            isGeoFocused,
            overlayCount,
            catchment,
            selAggLayer, // Pass showHex to control fill opacity
        });
        if (geoLayer) layers.push(geoLayer);

        const pathLayer = DeckPathLayer({
            geoJSON,
            selectedGeo,
            selectedTab,
            isChecked,
            catchment,
        });
        if (pathLayer) layers.push(pathLayer);

        // Add interactive layers (POI/MBP) AFTER geojson so they get picking priority
        const poiLayers = PoILayer({
            showPoi,
            poi,
            onHoverPoi,
            handleClick,
            focusedItem,
            isGeoFocused,
            brand,
        });
        if (poiLayers?.length) layers.push(...poiLayers);

        const mbpLayer = MBPLayer({
            showMbp,
            mbp,
            onHoverPoi,
            handleClick,
            focusedItem,
            isGeoFocused,
            hoveredPresence: presence,
        });
        if (mbpLayer) layers.push(mbpLayer);

        const storyLayer = StoryLayer({
            showStories,
            stories: stories || [],
            selectedStory,
            pathname,
        });
        if (storyLayer) layers.push(storyLayer);

        overlay.setProps({ layers });
    }, [showPoi, showMbp, poi, mbp, geoJSON, onHoverPoi, handleClick, focusedItem, overlay, map, selectedGeo, selectedTab, poiColors, cifType, brand, presence, selectedStory, isChecked?.subregionBoundaries, isChecked?.adjacency, mapProps, overlayCount, isChecked?.catchments, selAggLayer]);

    return null;
};
